import { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EVENT_CATEGORIES } from "@/lib/categories";
import { cn } from "@/lib/utils";

interface CategoryCarouselProps {
  className?: string;
}

export function CategoryCarousel({ className }: CategoryCarouselProps) {
  const { t } = useTranslation("landingHome");
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const scrollAmount = 200;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <section className={cn("py-8 md:py-16", className)}>
      <div className="container">
        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold">
              {t("categories.title")}
            </h2>
            <p className="text-muted-foreground mt-1 text-sm md:text-base">{t("categories.subtitle")}</p>
          </div>
          <Link
            to="/eventos"
            className="text-sm font-semibold text-primary hover:text-primary/80 transition-all flex items-center gap-1 group"
          >
            {t("categories.viewAll")} <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        {/* Carousel container */}
        <div className="relative group">
          {/* Navigation buttons - desktop only */}
          <Button
            variant="outline"
            size="icon"
            className="absolute -left-5 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-background/95 backdrop-blur shadow-xl border-border opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex active:scale-95"
            onClick={() => scroll("left")}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="absolute -right-5 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-background/95 backdrop-blur shadow-xl border-border opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex active:scale-95"
            onClick={() => scroll("right")}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          {/* Scrollable categories - contained within parent */}
          <div
            ref={scrollRef}
            className="flex gap-4 md:gap-6 overflow-x-auto no-scrollbar pb-6 pl-1"
            style={{ scrollSnapType: "x mandatory" }}
          >
            {EVENT_CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <Link
                  key={category.value}
                  to={`/eventos?categoria=${category.value}`}
                  className="flex-shrink-0 scroll-snap-align-start first:ml-0 last:mr-4"
                  style={{ scrollSnapAlign: "start" }}
                >
                  <div className="flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-card border border-border hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 min-w-[110px] md:min-w-[140px] group/item">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-muted/50 flex items-center justify-center group-hover/item:bg-primary/10 transition-colors shadow-inner">
                      <Icon className="h-6 w-6 md:h-8 md:w-8 text-muted-foreground group-hover/item:text-primary transition-all duration-300 transform group-hover/item:scale-110" />
                    </div>
                    <span className="text-xs md:text-sm font-bold text-center text-foreground/80 group-hover/item:text-primary transition-colors whitespace-nowrap tracking-tight">
                      {category.label}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
